import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LANDING_ROUTE, safeReturnPath } from '../../utils/roleLanding';
import CreateInstitution from '../institutions/CreateInstitution';
import TwoFactorVerify from './TwoFactorVerify';
import TwoFactorEnroll from './TwoFactorEnroll';
import {
    Alert,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Divider,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface LoginFormProps {
    onLoginSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showCreateInstitutionModal, setShowCreateInstitutionModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [challenge, setChallenge] = useState<{ type: 'OTP' | 'ENROLLMENT'; challengeId: string; qrDataUri?: string; secret?: string } | null>(null);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // A deep link the user was bounced from wins (route guards' state.from, or the auth
    // interceptor's ?from= after a session expiry); otherwise land by role. Only a same-origin
    // app path survives safeReturnPath (no open redirect, never back to /login).
    const postLoginPath = safeReturnPath((location.state as { from?: unknown } | null)?.from)
        ?? safeReturnPath(new URLSearchParams(location.search).get('from'))
        ?? LANDING_ROUTE;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await login({ username, password });

            if (result.success) {
                if (onLoginSuccess) {
                    onLoginSuccess();
                }
                navigate(postLoginPath);
            } else if (result.requiresTwoFactor && result.challengeId) {
                // For ENROLLMENT challenges, validate that qrDataUri and secret are present
                if (result.requiresTwoFactor === 'ENROLLMENT' && (!result.qrDataUri || !result.secret)) {
                    setError('Unable to start two-factor enrollment. Please try again or contact support.');
                } else {
                    setChallenge({
                        type: result.requiresTwoFactor,
                        challengeId: result.challengeId,
                        qrDataUri: result.qrDataUri,
                        secret: result.secret,
                    });
                }
            } else {
                setError(result.error || 'An error occurred during login');
            }
        } catch (err: any) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(err.response?.data?.message || 'Invalid credentials');
            }
            console.error('Login error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateInstitutionSuccess = (message?: string, email?: string) => {
        setShowCreateInstitutionModal(false);
        let successMsg = message || 'Institution setup created successfully! Your onboarded institution login credentials will be sent later once institution is been approved.';
        if (email) {
            successMsg += ` Login credentials will be sent to: ${email}`;
        }
        setSuccessMessage(successMsg);
    };

    if (challenge?.type === 'OTP') {
        return (
            <TwoFactorVerify
                challengeId={challenge.challengeId}
                onSuccess={() => { if (onLoginSuccess) onLoginSuccess(); navigate(postLoginPath); }}
                onCancel={() => setChallenge(null)}
            />
        );
    }

    if (challenge?.type === 'ENROLLMENT' && challenge.qrDataUri && challenge.secret) {
        return (
            <TwoFactorEnroll
                challengeId={challenge.challengeId}
                qrDataUri={challenge.qrDataUri}
                secret={challenge.secret}
                onSuccess={() => { if (onLoginSuccess) onLoginSuccess(); navigate(postLoginPath); }}
                onCancel={() => setChallenge(null)}
            />
        );
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="auth.login.page">
            <Typography variant="h5" fontWeight={600} mb={2}>
                Login
            </Typography>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <TextField
                    label="Username"
                    data-help="auth.login.field.username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder="Enter username"
                />
                <TextField
                    label="Password"
                    data-help="auth.login.field.password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder="Enter password"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                        data-help="auth.login.showPassword"
                                        aria-label={showPassword ? 'hide password' : 'show password'}
                                    >
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Button
                    type="submit"
                    data-help="auth.login.submit"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                        backgroundColor: '#007bff',
                        '&:hover': { backgroundColor: '#0056b3' },
                        textTransform: 'none',
                        py: 1,
                    }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" mb={1}>
                    Don't have an institution yet?
                </Typography>
                <Button
                    data-help="auth.login.createInstitution"
                    variant="contained"
                    onClick={() => setShowCreateInstitutionModal(true)}
                    sx={{
                        backgroundColor: '#28a745',
                        '&:hover': { backgroundColor: '#218838' },
                        textTransform: 'none',
                    }}
                >
                    Create New Institution
                </Button>
            </Box>

            <CreateInstitution
                isModal={true}
                open={showCreateInstitutionModal}
                onClose={() => setShowCreateInstitutionModal(false)}
                onSuccess={handleCreateInstitutionSuccess}
            />
        </Box>
    );
};

export default LoginForm;
